import {
  ClientContact,
  TicketDetailsModel,
  UpdateTicketPayload,
} from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { Avatar, TableCell } from "@jugl-web/ui-kit";
import { useToast } from "@jugl-web/utils";
import { NewContactDialog } from "@web-src/modules/client-support/components/NewContactDialog";
import { TicketContactPickerPopover } from "@web-src/modules/client-support/components/TicketContactPickerPopover/TicketContactPickerPopover";
import { UpdateTicketContactDialog } from "@web-src/modules/client-support/components/UpdateTicketContactDialog";
import { useUpdateTicketContactDialog } from "@web-src/modules/client-support/hooks/useUpdateTicketContactDialog";
import { FC, useCallback, useState } from "react";

interface ContactCellContentProps {
  ticket: TicketListItemModel;
  onUpdate: (
    data: UpdateTicketPayload
  ) => Promise<TicketDetailsModel | undefined>;
}

export const ContactCellContent: FC<ContactCellContentProps> = ({
  ticket,
  onUpdate,
}) => {
  const [isNewContactDialogOpen, setIsNewContactDialogOpen] = useState(false);

  const { toast } = useToast();

  const updateTicketContact = useCallback(
    async (contactId: string) => {
      await onUpdate({ contact_id: contactId });
      toast("Ticket customer has been updated");
    },
    [onUpdate, toast]
  );

  const { updateTicketContactDialogProps, requestUpdateContact } =
    useUpdateTicketContactDialog({
      currentContact: ticket.contact,
      onContactUpdate: updateTicketContact,
    });

  const handleContactCreated = (createdContact: ClientContact) => {
    requestUpdateContact({
      id: createdContact.id,
      name: createdContact.name ?? "Unknown",
    });
  };

  return (
    <>
      <TicketContactPickerPopover
        contact={ticket.contact}
        onChange={requestUpdateContact}
        onNewContactSelect={() => setIsNewContactDialogOpen(true)}
        renderTrigger={({ Trigger, triggerRef, isOpen }) => (
          <Trigger
            as={TableCell}
            ref={triggerRef}
            isFocused={isOpen}
            isHoverable
            classNames={{ content: "flex items-center gap-2 truncate" }}
          >
            <Avatar size="sm" imageUrl={null} username={ticket.contact.name} />
            <span className="truncate text-[13px] leading-[140%] font-normal text-dark">
              {ticket.contact.name}
            </span>
          </Trigger>
        )}
      />
      <UpdateTicketContactDialog {...updateTicketContactDialogProps} />
      <NewContactDialog
        isOpen={isNewContactDialogOpen}
        onRequestClose={() => setIsNewContactDialogOpen(false)}
        onContactCreated={handleContactCreated}
      />
    </>
  );
};
